import * as Sentry from '@sentry/react'

const MAX_ATTEMPTS = 3
const RETRY_DELAY_MS = 1000

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, milliseconds))

// Rate limits and upstream failures resolve on their own, so they are worth
// another attempt. Every other status is a decision RevenueCat made about this
// particular receipt and will be repeated identically.
const isRetryableStatus = (status: number) => status === 429 || status >= 500

// RevenueCat answers 529 while another ingestion of the same fetch_token is
// still in flight. On Stripe that other ingestion is RevenueCat's own
// integration, which picks the subscription up from its `app_user_id` metadata
// and finishes on its own, so the receipt is already being registered and this
// request has nothing left to contribute.
//
// Matched on the message and not the status alone, because 529 otherwise means
// the API is overloaded, which does deserve a retry.
const isAlreadyBeingIngested = (status: number, body: string) =>
  status === 529 && body.includes('currently being ingested')

export default async function createRevenueCatPurchase({
  subscriptionId,
  userId,
}: {
  subscriptionId: string
  userId: string
}) {
  for (let attempt = 1; ; attempt += 1) {
    const response = await fetch('https://api.revenuecat.com/v1/receipts', {
      body: JSON.stringify({
        app_user_id: userId,
        fetch_token: subscriptionId,
      }),
      headers: {
        Authorization: `Bearer ${import.meta.env.VITE_REVENUECAT_API_KEY}`,
        'Content-Type': 'application/json',
        'X-Platform': 'stripe',
      },
      method: 'POST',
    })

    if (response.ok) return response.json()

    // The status and body are the only place RevenueCat states why it refused
    // the receipt, so they are reported rather than discarded: the customer has
    // already paid at this point, and without an entitlement the purchase is
    // invisible to the app.
    const responseBody = await response.text()

    // Retrying here would only race the same ingestion again, and reporting it
    // would bury the failures that do cost a customer their entitlement.
    if (isAlreadyBeingIngested(response.status, responseBody)) return null

    if (isRetryableStatus(response.status) && attempt < MAX_ATTEMPTS) {
      await wait(RETRY_DELAY_MS * attempt)
      continue
    }

    Sentry.captureException(
      `Creating RevenueCat purchase failed with status=${response.status} attempts=${attempt} app_user_id=${userId} fetch_token=${subscriptionId} response=${responseBody}`,
    )

    return null
  }
}
